import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import InventoryIcon from '@mui/icons-material/Inventory'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid2,
  Icon,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Container } from '@mui/system'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment, { Moment } from 'moment'
import React, { useState } from 'react'
import { Link as ReactRouterLink, useParams } from 'react-router-dom'
import CommonPageWrapper from '../../common/components/CommonPageWrapper'
import NewEditButton from '../../common/iconButtons/NewEditButton'
import Loader from '../../common/loader/Loader'
import { Query } from '../../react-apollo/Components'
import { i18n } from '../index'
import { expressCheckoutPageQuery, fetchRestaurantMenuCategory } from './expressCheckoutQueries'
import TimeCell from './TimeCell'
import { Category, CategoryProduct, OpeningHoursPeriod, RestaurantCategorySchedule, RestaurantMenu } from './types'
import {
  DATE_HEADER_FORMAT,
  getDateHeaders,
  getOpeningHoursForDate,
  sortCategories,
  TODAY_HIGHLIGHT_COLOR,
} from './util'

export default function ExpressCheckoutOpeningHoursPage() {
  const { stationId } = useParams<{ stationId: string }>()
  const initialStartDate = moment().startOf('week')
  const [dateSelected, setDateSelected] = useState<Moment>(initialStartDate)
  const [displayedCategories, setDisplayedCategories] = useState<string[]>([])
  const endDate = dateSelected.clone().endOf('week')
  const dateHeaders = getDateHeaders(dateSelected, endDate)
  const tableHeaders: string[] = [i18n.t('restaurantMenu.category'), ...dateHeaders]
  if (!stationId) {
    return <CommonPageWrapper title={i18n.t('restaurantMenu.openingHours')} />
  }

  function onDateChange(newDate: Moment | null) {
    if (newDate) {
      setDateSelected(newDate.startOf('week'))
    }
  }

  return (
    <CommonPageWrapper title={i18n.t('restaurantMenu.openingHours')}>
      <Grid2 container justifyContent='center' alignItems='center'>
        <Grid2>
          <Typography variant='h4'>{i18n.t('restaurantMenu.openingHours')}</Typography>
        </Grid2>
      </Grid2>
      <Query<
        {
          expressCheckoutMenu?: RestaurantMenu
          expressCheckoutSchedules?: RestaurantCategorySchedule[]
          expressCheckoutCategoryPeriods?: OpeningHoursPeriod[]
        },
        { stationId: string }
      >
        query={expressCheckoutPageQuery}
        variables={{ stationId: decodeURIComponent(stationId) }}
      >
        {({ loading, data }) => {
          const menuCategories: Category[] = data?.expressCheckoutMenu?.categories ?? []
          const filteredCategories = displayedCategories.length
            ? menuCategories.filter((category) => displayedCategories.includes(category.id))
            : menuCategories
          const sortedCategories = sortCategories(filteredCategories)
          return (
            <Loader loading={loading}>
              {menuCategories.length > 0 && (
                <>
                  <ExpressCheckoutActions
                    stationId={stationId}
                    dateSelected={dateSelected}
                    onDateSelected={onDateChange}
                    categories={menuCategories}
                    displayedCategories={displayedCategories}
                    setDisplayedCategories={setDisplayedCategories}
                  />
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {tableHeaders.map((header) => {
                            const isDateHeader = dateHeaders.includes(header)
                            if (isDateHeader && moment(header, DATE_HEADER_FORMAT).isSame(moment(), 'day')) {
                              return (
                                <TableCell
                                  key={header}
                                  style={{ fontWeight: 'bold', backgroundColor: TODAY_HIGHLIGHT_COLOR }}
                                >
                                  {header}
                                </TableCell>
                              )
                            }
                            return <TableCell key={header}>{header}</TableCell>
                          })}
                          <TableCell>{i18n.t('restaurantMenu.editShowProducts')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedCategories.map((category: Category) => (
                          <CategoryRow
                            key={category.id}
                            stationId={stationId}
                            category={category}
                            dateHeaders={dateHeaders}
                            dateSelected={dateSelected}
                            schedules={data?.expressCheckoutSchedules}
                            periods={data?.expressCheckoutCategoryPeriods}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
              {menuCategories.length < 1 && !loading && (
                <Grid2 container justifyContent='center' sx={{ marginTop: '40px' }}>
                  <Grid2>
                    <Typography variant='h5'>{i18n.t('restaurantMenu.errorMessages.dataNotFound')}</Typography>
                  </Grid2>
                </Grid2>
              )}
            </Loader>
          )
        }}
      </Query>
    </CommonPageWrapper>
  )
}

interface CategoryRowProps {
  category: Category
  stationId: string
  dateHeaders: string[]
  dateSelected: Moment
  schedules?: RestaurantCategorySchedule[]
  periods?: OpeningHoursPeriod[]
}

function CategoryRow({ stationId, category, dateHeaders, dateSelected, schedules, periods }: CategoryRowProps) {
  const [modalOpen, setModalOpen] = useState(false)

  return (
    <TableRow key={category.id}>
      <TableCell style={{ fontWeight: 'bold' }}>{category.name}</TableCell>
      {dateHeaders.map((date) => {
        const dateOfHeader = moment(date, DATE_HEADER_FORMAT).startOf('day')
        const hours = getOpeningHoursForDate(dateOfHeader, category, schedules, periods)
        const isToday = dateOfHeader.isSame(moment().startOf('day'))
        return (
          <TimeCell
            key={date}
            schedules={hours.schedules}
            isExceptional={hours.isExceptional}
            isToday={isToday}
            isDefaultTime={hours.isDefaultTime}
          />
        )
      })}
      <TableCell align='center'>
        <NewEditButton
          component={ReactRouterLink}
          to={`/pikakassa/${stationId}/aukioloajat/${category.id}`}
          size='small'
          title={i18n.t('restaurantMenu.edit')}
        />
        <IconButton
          size='small'
          onClick={() => setModalOpen(true)}
          title={i18n.t('restaurantMenu.categoryProductModal.title')}
        >
          <InventoryIcon />
        </IconButton>
        <CategoryProductModal
          stationId={stationId}
          categoryId={category.id}
          categoryName={category.name}
          open={modalOpen}
          onClose={() => {
            setModalOpen(false)
          }}
        />
      </TableCell>
    </TableRow>
  )
}

interface ExpressCheckoutActionsProps {
  stationId: string
  dateSelected?: Moment | null
  categories?: Category[]
  displayedCategories: string[]
  setDisplayedCategories: (categories: string[]) => void
  onDateSelected?: (newDate: Moment | null) => void
}

function ExpressCheckoutActions({
  stationId,
  dateSelected,
  categories = [],
  onDateSelected,
  setDisplayedCategories,
  displayedCategories,
}: ExpressCheckoutActionsProps) {
  const [filterDialogOpen, setFilterDialogOpen] = useState(false)

  const weekNumber = dateSelected ? dateSelected.week() : moment().week()

  function weekBack() {
    if (dateSelected && onDateSelected) {
      const newDate = dateSelected.clone().subtract(7, 'days')
      onDateSelected(newDate)
    }
  }

  function weekForward() {
    if (dateSelected && onDateSelected) {
      const newDate = dateSelected.clone().add(7, 'days')
      onDateSelected(newDate)
    }
  }

  return (
    <Container sx={{ marginTop: '20px', marginBottom: '20px' }}>
      <Grid2 direction='column' container spacing={2}>
        <Grid2 container direction='row' spacing={2} justifyContent='center'>
          <Grid2>
            <IconButton onClick={weekBack}>
              <ArrowBackIcon />
            </IconButton>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DesktopDatePicker
                displayWeekNumber
                value={dateSelected}
                format={`${i18n.t('common.week')} ${weekNumber}`}
                onChange={onDateSelected}
                views={['year', 'month', 'day']}
                slotProps={{
                  textField: { variant: 'outlined', inputProps: { readOnly: true } },
                  popper: {
                    placement: 'bottom',
                    modifiers: [
                      {
                        name: 'offset',
                        options: { offset: [0, 8] },
                      },
                    ],
                  },
                }}
              />
            </LocalizationProvider>
            <IconButton onClick={weekForward}>
              <ArrowForwardIcon />
            </IconButton>
          </Grid2>
        </Grid2>
        <Grid2 direction='row' spacing={2} container justifyContent='center'>
          <Grid2>
            <Button
              variant='contained'
              onClick={() => setFilterDialogOpen(true)}
              startIcon={
                <Icon>
                  <FilterAltIcon />
                </Icon>
              }
            >
              {i18n.t('restaurantMenu.filter')}
            </Button>
          </Grid2>
          <Grid2>
            <Button
              startIcon={<AddIcon />}
              component={ReactRouterLink}
              variant='contained'
              to={`/pikakassa/${stationId}/poikkeusaukioloajat`}
            >
              {i18n.t('restaurantMenu.exceptionalSchedules')}
            </Button>
          </Grid2>
        </Grid2>
      </Grid2>
      <FilterDialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        categories={categories}
        displayedCategories={displayedCategories}
        setDisplayedCategories={setDisplayedCategories}
      />
    </Container>
  )
}

interface FilterDialogProps {
  open: boolean
  categories: Category[]
  displayedCategories: string[]
  setDisplayedCategories: (categories: string[]) => void
  onClose: () => void
}

function FilterDialog({ open, onClose, categories, displayedCategories, setDisplayedCategories }: FilterDialogProps) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{i18n.t('restaurantMenu.filter')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {categories.map((category) => (
            <FormControlLabel
              key={category.id}
              control={
                <Checkbox
                  checked={displayedCategories.length === 0 || displayedCategories.includes(category.id)}
                  onChange={(event) => {
                    if (event.target.checked) {
                      setDisplayedCategories([...displayedCategories, category.id])
                    } else {
                      if (displayedCategories.length === 0) {
                        // All categories are currently displayed, so we need to add all except the one being unchecked
                        setDisplayedCategories(categories.filter((c) => c.id !== category.id).map((c) => c.id))
                      } else {
                        setDisplayedCategories(displayedCategories.filter((id) => id !== category.id))
                      }
                    }
                  }}
                />
              }
              label={category.name}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{i18n.t('buttons.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}

interface CategoryProductModalProps {
  open: boolean
  stationId: string
  categoryId: string
  categoryName: string
  onClose: () => void
}

function CategoryProductModal({ open, onClose, stationId, categoryId, categoryName }: CategoryProductModalProps) {
  const toAvailabilityText = (assortment?: string | null) => {
    switch (assortment) {
      case 'AVAILABLE':
        return i18n.t('restaurantMenu.categoryProductModal.availabilityYes')
      case 'UNAVAILABLE':
        return i18n.t('restaurantMenu.categoryProductModal.availabilityNo')
      default:
        return i18n.t('restaurantMenu.categoryProductModal.noAvailabilityInformation')
    }
  }

  const toTargetText = (targets?: string[] | null) => {
    if (!targets || targets.length === 0) {
      return i18n.t('restaurantMenu.categoryProductModal.unknown')
    }
    return targets
      .map((target) => {
        switch (target) {
          case 'EXPRESS_CHECKOUT':
            return i18n.t('restaurantMenu.categoryProductModal.expressCheckout')
          case 'ABC_MOBILE':
            return i18n.t('restaurantMenu.categoryProductModal.abcMobile')
          default:
            return i18n.t('restaurantMenu.categoryProductModal.unknown')
        }
      })
      .join(', ')
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{`${i18n.t('restaurantMenu.categoryProductModal.title')} - ${categoryName}`}</DialogTitle>
      <DialogContent>
        <Query<
          {
            restaurantMenuCategory?: Category
          },
          { stationId: string; categoryId: string }
        >
          query={fetchRestaurantMenuCategory}
          variables={{ stationId: decodeURIComponent(stationId), categoryId }}
        >
          {({ loading, data }) => {
            const category = data?.restaurantMenuCategory
            const products = category?.products ?? []
            return (
              <Loader loading={loading}>
                {category && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>{i18n.t('restaurantMenu.categoryProductModal.productId')}</TableCell>
                          <TableCell>{i18n.t('restaurantMenu.categoryProductModal.productName')}</TableCell>
                          <TableCell>{i18n.t('restaurantMenu.categoryProductModal.AAPAavailability')}</TableCell>
                          <TableCell>{i18n.t('restaurantMenu.categoryProductModal.target')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {products.map((product: CategoryProduct) => (
                          <TableRow key={product.id}>
                            <TableCell>{product.id}</TableCell>
                            <TableCell>
                              {product.name || i18n.t('restaurantMenu.categoryProductModal.missingName')}
                            </TableCell>
                            <TableCell>{toAvailabilityText(product.assortment)}</TableCell>
                            <TableCell>{toTargetText(product.targets)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Loader>
            )
          }}
        </Query>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{i18n.t('buttons.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}
